import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { ObjectUploader } from "@/components/ObjectUploader";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Sparkles, Upload } from "lucide-react";
import { AIImageGeneratorDialog } from "./ai-image-generator-dialog";

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

interface MediaUploaderCardProps {
  className?: string;
}

interface UploadParamsResponse {
  uploadURL: string;
  objectPath: string;
  uploadToken: string;
}

// Lightweight policy shape — we only read aiEnabled + imageEnabled here so the
// "Generate with AI" button can hide itself when the workspace has the feature
// turned off (avoids an opening-the-dialog-only-to-see-disabled UX).
type PolicyResponse = {
  aiEnabled: boolean;
  policy: { imageEnabled: boolean; nanoBananaEnabled: boolean; runwayEnabled: boolean };
};

export function MediaUploaderCard({ className }: MediaUploaderCardProps) {
  const { toast } = useToast();
  const [genOpen, setGenOpen] = useState(false);
  const policy = useQuery<PolicyResponse>({ queryKey: ["/api/social/ai-images/policy"] });
  const aiAvailable = !!(
    policy.data?.aiEnabled &&
    policy.data?.policy.imageEnabled &&
    (policy.data.policy.nanoBananaEnabled || policy.data.policy.runwayEnabled)
  );

  const finalize = useMutation({
    mutationFn: async (payload: {
      uploadToken: string;
      fileName: string;
    }) => {
      const res = await apiRequest("POST", "/api/social/media", payload);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/social/media"] });
      queryClient.invalidateQueries({ queryKey: ["/api/social/storage/quota"] });
      toast({ title: "Media uploaded", description: "It is now in your library." });
    },
    onError: (err: any) => {
      toast({
        title: "Could not save uploaded file",
        description: err?.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  return (
    <div className="inline-flex items-center gap-2">
      <ObjectUploader
        maxNumberOfFiles={5}
        maxFileSize={MAX_BYTES}
        buttonClassName={className}
        onGetUploadParameters={async (file) => {
          const res = await apiRequest("POST", "/api/social/media/upload-url", {
            name: file.name,
            size: file.size,
            contentType: file.type || "application/octet-stream",
          });
          const data = (await res.json()) as UploadParamsResponse;
          // Stash the signed upload token on the file so finalize can present it.
          (file as any).meta = {
            ...((file as any).meta || {}),
            uploadToken: data.uploadToken,
          };
          return { method: "PUT", url: data.uploadURL };
        }}
        onComplete={(result) => {
          for (const f of result.successful || []) {
            const uploadToken = (f as any).meta?.uploadToken as string | undefined;
            if (!uploadToken) continue;
            finalize.mutate({
              uploadToken,
              fileName: f.name || "untitled",
            });
          }
        }}
      >
        <span className="inline-flex items-center gap-2">
          <Upload className="h-4 w-4" />
          Upload media
        </span>
      </ObjectUploader>

      {aiAvailable && (
        <Button
          type="button"
          variant="outline"
          onClick={() => setGenOpen(true)}
          data-testid="button-open-ai-image-library"
        >
          <Sparkles className="h-4 w-4 mr-2" />
          Generate with AI
        </Button>
      )}

      <AIImageGeneratorDialog open={genOpen} onOpenChange={setGenOpen} />
    </div>
  );
}
